import { api } from "./api/client.js";

export type PushSupport = "unsupported" | "no-worker" | "ready";

/**
 * Notifications need the Push API and a registered service worker. The worker is only registered
 * in production builds, so development reports "no-worker".
 */
export async function pushSupport(): Promise<PushSupport> {
  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }
  const registration = await navigator.serviceWorker.getRegistration();
  return registration ? "ready" : "no-worker";
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

/** Asks for permission, subscribes this browser and registers it with the API. */
export async function enablePush(publicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new PushPermissionError(permission);
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    }));
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error("The browser returned an incomplete push subscription");
  }
  await api.push.subscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
}

export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  await api.push.unsubscribe(subscription.endpoint).catch(() => undefined);
  await subscription.unsubscribe();
}

/**
 * Runs before sign-out: the next person using this browser must not receive the previous user's
 * mail notifications. Never blocks sign-out for more than a moment.
 */
export async function disablePushBeforeSignOut(): Promise<void> {
  await Promise.race([
    disablePush().catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, 1_500)),
  ]);
}

export class PushPermissionError extends Error {
  constructor(readonly permission: NotificationPermission) {
    super(`Notification permission is ${permission}`);
    this.name = "PushPermissionError";
  }
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = `${value}${"=".repeat((4 - (value.length % 4)) % 4)}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
